import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Download, TrendingUp, Wallet, Clock, AlertTriangle, ChevronRight } from "lucide-react";
import { partsSummary } from "@/components/PartsCostEditor";
import { Link } from "react-router-dom";

const TABS = [
  { id: "production", label: "Producción", icon: TrendingUp },
  { id: "payments", label: "Cobros", icon: Wallet },
  { id: "pending", label: "Pendientes de retiro", icon: Clock },
];

function ymNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmt(n) {
  return n == null ? "—" : `$${Number(n).toFixed(2)}`;
}

export default function AdminReports() {
  const [tab, setTab] = useState("production");
  const [month, setMonth] = useState(ymNow());
  const [technicianId, setTechnicianId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [technicians, setTechnicians] = useState([]);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setTechnicians(r.data.filter((u) => u.role === "technician"))).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const commonParams = useMemo(() => {
    const p = { month };
    if (technicianId) p.technician_id = technicianId;
    if (branchId) p.branch_id = branchId;
    if (categoryId) p.category_id = categoryId;
    if (serviceId) p.service_id = serviceId;
    return p;
  }, [month, technicianId, branchId, categoryId, serviceId]);

  async function load() {
    setLoading(true);
    setData(null);
    try {
      const path = tab === "pending" ? "/admin/reports/pending-pickup" : `/admin/reports/${tab}`;
      const params = tab === "pending" ? {} : commonParams;
      const r = await api.get(path, { params });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, month, technicianId, branchId, categoryId, serviceId]);

  const csvUrl = useMemo(() => {
    const base = process.env.REACT_APP_BACKEND_URL + "/api";
    if (tab === "pending") return `${base}/admin/reports/pending-pickup.csv`;
    const qs = new URLSearchParams(commonParams).toString();
    return `${base}/admin/reports/${tab}.csv?${qs}`;
  }, [tab, commonParams]);

  async function downloadCsv() {
    try {
      const r = await api.get(csvUrl.replace(process.env.REACT_APP_BACKEND_URL + "/api", ""), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = tab === "pending" ? "pendientes_retiro.csv" : `${tab}_${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="font-heading font-extrabold text-3xl text-slate-900 flex items-center gap-2">
          <BarChart3 size={28} className="text-blue-600" /> Estadísticas
        </h1>
        <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Solo administrador</span>
      </header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`report-tab-${t.id}`}
            className={`px-4 py-2.5 font-semibold text-sm border-b-2 -mb-px whitespace-nowrap transition-all flex items-center gap-1.5 ${
              tab === t.id ? "text-blue-700 border-blue-500" : "text-slate-500 border-transparent hover:text-slate-800"
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card-base p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {tab !== "pending" && (
          <div>
            <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Mes</label>
            <input type="month" className="input-base" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" />
          </div>
        )}
        {tab !== "pending" && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Técnico</label>
              <select className="input-base" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)} data-testid="report-technician">
                <option value="">Todos</option>
                {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Sucursal</label>
              <select className="input-base" value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="report-branch">
                <option value="">Todas</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Categoría</label>
              <select className="input-base" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="report-category">
                <option value="">Todas</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Servicio</label>
              <select className="input-base" value={serviceId} onChange={(e) => setServiceId(e.target.value)} data-testid="report-service">
                <option value="">Todos</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </>
        )}
        <div className={tab === "pending" ? "" : "sm:col-span-2 md:col-span-3 lg:col-span-1 flex items-end"}>
          <button className="btn-primary w-full min-h-[42px]" onClick={downloadCsv} data-testid="report-download-csv">
            <Download size={16} /> Exportar CSV
          </button>
        </div>
      </div>

      {loading && <div className="text-center text-slate-500 py-6">Cargando…</div>}

      {!loading && data && (
        <div className="space-y-4">
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="report-kpis">
            <Kpi label="Reparaciones" value={data.kpis?.count ?? 0} testid="kpi-count" />
            <Kpi
              label={tab === "payments" ? "Cobrado" : "Vendido"}
              value={fmt(tab === "payments" ? data.kpis?.paid_amount_total : data.kpis?.sale_total)}
              accent="emerald"
              testid="kpi-sale"
            />
            {tab === "production" && <Kpi label="Costos" value={fmt(data.kpis?.cost_total)} accent="amber" testid="kpi-cost" />}
            {tab === "production" && <Kpi label="Ganancia" value={fmt(data.kpis?.profit_total)} accent="blue" testid="kpi-profit" />}
            {tab === "payments" && <Kpi label="Ganancia" value={fmt(data.kpis?.profit_total)} accent="blue" testid="kpi-profit" />}
            {tab === "payments" && <Kpi label="Vendido (producción)" value={fmt(data.kpis?.sale_total)} accent="slate" testid="kpi-sale-prod" />}
            {tab === "pending" && (
              <>
                <Kpi label="Días de espera" value={data.rows?.length ? `≥ ${Math.max(...data.rows.map((r) => r.days_since_finalized || 0))}` : "0"} accent="amber" testid="kpi-days" />
                <div />
              </>
            )}
          </div>

          {data.kpis?.missing_cost_count > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-sm text-amber-900" data-testid="report-missing-cost">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <b>{data.kpis.missing_cost_count}</b> reparaciones sin costo configurado — la ganancia no se puede calcular hasta asignar el costo.{" "}
                <Link to="/admin/costs" className="underline font-semibold">Configurar costos</Link>
              </div>
            </div>
          )}

          {/* Breakdown */}
          {(data.by_technician?.length || data.by_branch?.length || data.by_category?.length) ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {data.by_technician?.length ? (
                <BreakdownCard title="Por técnico" rows={data.by_technician} testid="breakdown-tech" showCommission={tab === "production"} />
              ) : null}
              {data.by_branch?.length ? (
                <BreakdownCard title="Por sucursal" rows={data.by_branch} testid="breakdown-branch" />
              ) : null}
              {data.by_category?.length ? (
                <BreakdownCard title="Por categoría" rows={data.by_category} testid="breakdown-cat" />
              ) : null}
            </div>
          ) : null}

          {/* Detail table */}
          <div className="card-base overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-baseline justify-between">
              <h3 className="font-heading font-bold text-slate-900">Detalle</h3>
              <span className="text-xs text-slate-500 font-mono">{data.rows?.length || 0} reparaciones</span>
            </div>
            {(!data.rows || data.rows.length === 0) ? (
              <div className="text-center text-slate-500 py-10">Sin datos para los filtros seleccionados.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="report-detail-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-left">
                      <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Orden</th>
                      <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Cliente</th>
                      <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Equipo</th>
                      <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Técnico</th>
                      <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Sucursal</th>
                      <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">{tab === "payments" ? "Cobrado" : "Finalizada"}</th>
                      <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">Venta</th>
                      <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">Costo</th>
                      <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">Ganancia</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.order_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`report-row-${r.order_id}`}>
                        <td className="p-3 font-mono text-slate-900">{r.order_number}</td>
                        <td className="p-3">{r.customer_name || "—"}</td>
                        <td className="p-3">
                          {r.equipment || r.category_name || "—"}
                          {(r.items || []).filter((it) => !it.service_id).map((it) => (
                            <div key={it.id} className="text-[11px] text-slate-500 mt-0.5" data-testid={`report-custom-item-${it.id}`}>
                              <span className="font-semibold text-slate-700">{it.name}</span> · {fmt((it.unit_price || 0) * (it.quantity || 1))} · <span className={partsSummary(it).tone}>{partsSummary(it).text}</span>
                            </div>
                          ))}
                        </td>
                        <td className="p-3">{r.technician_name || <span className="text-slate-400">Sin asignar</span>}</td>
                        <td className="p-3">{r.branch_name || "—"}</td>
                        <td className="p-3 text-xs font-mono text-slate-600">
                          {tab === "payments"
                            ? (r.paid_at ? new Date(r.paid_at).toLocaleDateString() : "—")
                            : (r.finalized_at ? new Date(r.finalized_at).toLocaleDateString() : "—")}
                        </td>
                        <td className="p-3 text-right font-mono">{fmt(r.sale_total)}</td>
                        <td className="p-3 text-right font-mono">
                          {r.has_missing_cost ? <span className="text-amber-600 text-xs">Sin costo</span> : fmt(r.cost_total)}
                        </td>
                        <td className="p-3 text-right font-mono font-semibold">
                          {r.has_missing_cost ? "—" : (
                            <span className={(r.profit_total ?? 0) >= 0 ? "text-emerald-700" : "text-red-600"}>{fmt(r.profit_total)}</span>
                          )}
                          {r.breakdown && (
                            <div className="text-[10px] font-normal text-slate-500 whitespace-nowrap" data-testid={`report-split-${r.order_id}`}>
                              {r.breakdown.status === "OK"
                                ? `Téc ${fmt(r.breakdown.technician_payout)} · Emp ${fmt(r.breakdown.company_share)}`
                                : <span className="text-amber-600">Reparto pendiente</span>}
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          <Link to={`/orders/${r.order_id}`} className="text-blue-600 hover:text-blue-800">
                            <ChevronRight size={16} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent = "slate", testid }) {
  const map = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    blue: "text-blue-700",
  };
  return (
    <div className="card-base p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
      <div className={`font-heading font-extrabold text-2xl sm:text-3xl ${map[accent]}`}>{value}</div>
    </div>
  );
}

function BreakdownCard({ title, rows, testid, showCommission }) {
  return (
    <div className="card-base p-4" data-testid={testid}>
      <h4 className="font-heading font-bold mb-3 text-slate-900">{title}</h4>
      <ul className="divide-y divide-slate-100">
        {rows.slice(0, 8).map((r) => (
          <li key={String(r.id) + r.name} className="py-2 flex items-center justify-between gap-2" data-testid={`${testid}-row-${r.id || "none"}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate">{r.name}</div>
              <div className="text-[11px] text-slate-500 font-mono">{r.count} reparaciones · Venta ${r.sale_total.toFixed(2)}</div>
              {showCommission && r.commission_rate != null && (
                <div className="text-[11px] font-mono text-amber-700" data-testid={`${testid}-commission-${r.id || "none"}`}>
                  Comisión {r.commission_rate}% = <b>${(r.commission_total || 0).toFixed(2)}</b>
                </div>
              )}
              {showCommission && r.commission_rate == null && r.id && (
                <div className="text-[11px] text-slate-400 italic">Sin % de comisión configurado</div>
              )}
            </div>
            <div className="font-mono font-bold text-emerald-700 text-right">${r.profit_total.toFixed(2)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
